import { DEFAULT_CLIENT_REQUEST_ID, getTrimPaymentApiPath } from '../../apiHooks/utils';
import { isPaymentOptionsOverride, selectPrimaryCalculatorLoyaltyOfferKey, selectPrimaryCalculatorPaymentOptions, } from '../reducers/financialCalculators';
import { selectCanUseLoyalty } from '../reducers/offerOptions';
import { selectCurrentVehicle } from '../reducers/currentVehicle';
import { useApiHost, useAppName, usePageState, useSettings, useUserOrDefaultProvince, } from '../../utils/sitecoreContext';
import { useAppSelector } from '../utils';
import { useGetMultiplePaymentsQuery } from '../apis/hondaRestApi';
export const logRequestWarnings = (el) => {
    const vehicleName = `${el === null || el === void 0 ? void 0 : el.modelYear} ${el === null || el === void 0 ? void 0 : el.modelKey} ${el === null || el === void 0 ? void 0 : el.trimKey} ${el === null || el === void 0 ? void 0 : el.transmissionKey}`;
    if (!(el === null || el === void 0 ? void 0 : el.transmissionKey)) {
        // eslint-disable-next-line no-console
        console.warn(`Missing transmissionKey for ${vehicleName}`);
    }
    if (!(el === null || el === void 0 ? void 0 : el.exteriorColorKey)) {
        // eslint-disable-next-line no-console
        console.warn(`Missing exteriorColorKey for ${vehicleName}`);
    }
};
// Exclude a web catalog trim with no transmission selected, or a transmission with no exterior color selected
export const getValidatedRequestBody = (arr) => arr.filter(el => {
    if (!el.transmissionKey || !el.exteriorColorKey) {
        logRequestWarnings(el);
        return false;
    }
    return true;
});
/*
 * Note: To override a `requestObject.paymentOptions` property,
 * pass the corresponding property from `PAYMENT_OPTIONS_OVERRIDES` to `customPaymentOptions`.\
 * If no existing code handles overrides for your desired property,
 * then write your logic for detecting (using `isPaymentOptionsOverride`) and overriding it
 */
const useFetchMultiplePayments = (vehicles, shouldFetch, customPaymentOptions, vehicleType, provinceCode) => {
    const { defaultProvince } = useSettings();
    const provinceCodeFromUserOrDefaultProvince = useUserOrDefaultProvince();
    const pageState = usePageState();
    const hondaRestApiHost = useApiHost();
    const appName = useAppName();
    const apiPath = getTrimPaymentApiPath(appName, pageState, vehicleType, true);
    const url = `https://${hondaRestApiHost}/${apiPath}`;
    const primaryCalculatorPaymentOptions = useAppSelector(selectPrimaryCalculatorPaymentOptions);
    const offersDefault = useAppSelector(state => state.offers);
    const canUseLoyalty = useAppSelector(selectCanUseLoyalty);
    const primaryCalculatorLoyaltyOfferKey = useAppSelector(selectPrimaryCalculatorLoyaltyOfferKey);
    const { trimKey: currentTrimKey, transmissionKey: currentTransmissionKey } = useAppSelector(selectCurrentVehicle);
    const payloadBody = vehicles.map((vehicle, index) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const paymentOptions = (customPaymentOptions && customPaymentOptions[index]) || primaryCalculatorPaymentOptions;
        const canOverrideIncentiveOfferIds = isPaymentOptionsOverride('incentiveOfferIds', (_b = (_a = customPaymentOptions === null || customPaymentOptions === void 0 ? void 0 : customPaymentOptions[index]) === null || _a === void 0 ? void 0 : _a.incentiveOfferIds) !== null && _b !== void 0 ? _b : []);
        const incentiveOfferIds = (canOverrideIncentiveOfferIds && []) ||
            ((_d = (_c = offersDefault === null || offersDefault === void 0 ? void 0 : offersDefault[vehicle.transmissionKey || '']) === null || _c === void 0 ? void 0 : _c[paymentOptions.paymentMethod]) === null || _d === void 0 ? void 0 : _d.incentive) ||
            [];
        const defaultLoyaltyOfferKey = (_h = (_g = (_f = offersDefault === null || offersDefault === void 0 ? void 0 : offersDefault[(_e = vehicle.transmissionKey) !== null && _e !== void 0 ? _e : '']) === null || _f === void 0 ? void 0 : _f[paymentOptions.paymentMethod]) === null || _g === void 0 ? void 0 : _g.loyalty) !== null && _h !== void 0 ? _h : '';
        const loyaltyOfferKey = canUseLoyalty
            ? (vehicle.trimKey === currentTrimKey &&
                vehicle.transmissionKey === currentTransmissionKey &&
                primaryCalculatorLoyaltyOfferKey) ||
                paymentOptions.loyaltyOfferKey ||
                defaultLoyaltyOfferKey
            : '';
        const requestObject = {
            exteriorColorKey: vehicle.exteriorColorKey,
            interiorColorKey: vehicle.interiorColorKey,
            modelKey: vehicle.modelKey,
            modelYear: vehicle.modelYear,
            trimKey: vehicle.trimKey,
            transmissionKey: vehicle.transmissionKey,
            provinceKey: provinceCode || provinceCodeFromUserOrDefaultProvince || defaultProvince,
            warrantyAmount: paymentOptions.warrantyAmount,
            paymentOptions: [
                {
                    clientRequestId: DEFAULT_CLIENT_REQUEST_ID,
                    downPaymentAmount: paymentOptions.downPaymentAmount,
                    incentiveOfferIds,
                    includeFees: paymentOptions.includeFees,
                    leaseAdditionalAnnualKm: paymentOptions.leaseAdditionalAnnualKm,
                    leaseAnnualKmAllowance: paymentOptions.leaseAnnualKmAllowance,
                    loyaltyOfferKey,
                    overriddenIsSellingProvince: paymentOptions.overriddenIsSellingProvince,
                    paymentFrequency: paymentOptions.paymentFrequency,
                    paymentMethod: paymentOptions.paymentMethod,
                    term: paymentOptions.termAndRate,
                    tradeInOwingAmount: paymentOptions.tradeInOwingAmount,
                    tradeInValueAmount: paymentOptions.tradeInValueAmount,
                },
            ],
        };
        return requestObject;
    });
    const validatedRequestBody = getValidatedRequestBody(payloadBody);
    const { data, isFetching, isLoading, isSuccess, error, isUninitialized } = useGetMultiplePaymentsQuery({
        url,
        body: validatedRequestBody,
    }, { skip: !shouldFetch || validatedRequestBody.length === 0 });
    return {
        data,
        isFetching,
        isLoading,
        isSuccess,
        error,
        isUninitialized,
    };
};
export default useFetchMultiplePayments;
